import { useState, useEffect, useRef, useCallback } from "react";
import {
  getReportingQuestions,
  getOptimizedAnalyticsByLocation,
  getTriggers,
} from "./flagvaluesApi";
import {
  getCategories,
  monthToDateRange,
  getDaysInMonth,
  previousFinancialYearId,
  triggerForMonth,
  computeDifference,
  computeRemark,
  scopeKey,
  analyticsLocationParam,
} from "./helpers";

/**
 * Fetches and shapes a data-analytics tab (Bio-Medical or Water) for the
 * selected FY, month and reporting scopes. Rows come from the reporting-question
 * API filtered to `questionIds`; Actual is the month-ranged
 * `selected_date_range_value` from optimized-analytics; Min/Max come from the
 * covering trigger row for that scope.
 *
 * Analytics is scope-level: optimized-analytics-by-location returns one row per
 * (location, sub-location, question), so `byLocation` is keyed by `scopeKey` and
 * a source's sub-locations (KMC-T1 vs KMC-T2) each keep their own values instead
 * of sharing one site-wide aggregate. The fetch asks for exactly the scopes it
 * renders — `analyticsLocationParam` sends `sub_<id>` for a sub-location — so a
 * sub-location's request is never widened back out to its whole source.
 *
 * @param {Object} params
 * @param {number|null} params.financialYearId  pass null to keep the hook idle
 * @param {string} params.selectedMonth  `YYYY-MM`
 * @param {Array} params.locations  `{ id, subLocationId, name }[]`, one per scope
 * @param {Array} params.financialYear  the FY list (for previous-FY lookup)
 * @param {number[]} params.questionIds  which questions belong to this tab
 * @param {number[]} [params.extraAnalyticsQids]  extra qids to fetch (not rendered)
 *   — inputs a derived-trigger helper needs but that aren't displayed rows
 * @param {Function} [params.applyDerivedTriggers]  optional (rows, analyticsByQid)
 *   hook that fills in Min/Max for derived rows in place, per scope
 * @returns {{ categories: Array, byLocation: Object, loading: boolean,
 *   error: (string|null), refresh: Function }}
 *   `byLocation` is keyed by `scopeKey(id, subLocationId)` → { rows: Array<row> }.
 */
const NO_EXTRA_QIDS = [];

export const useAnalyticsData = ({
  financialYearId,
  selectedMonth,
  locations,
  financialYear,
  questionIds,
  extraAnalyticsQids = NO_EXTRA_QIDS,
  applyDerivedTriggers = null,
}) => {
  const [categories, setCategories] = useState([]);
  const [byLocation, setByLocation] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchIdRef = useRef(0);
  const locationsRef = useRef(locations);
  locationsRef.current = locations;

  const locationKey = (locations || [])
    .filter((l) => l && l.id != null)
    .map((l) => scopeKey(l.id, l.subLocationId))
    .join(",");

  const runFetch = useCallback(
    async (background = false) => {
      const scopes = (locationsRef.current || []).filter(
        (l) => l && l.id != null
      );

      const requestLocationIds = Array.from(
        new Set(scopes.map(analyticsLocationParam))
      );

      if (!financialYearId || !selectedMonth || scopes.length === 0) {
        setCategories([]);
        setByLocation({});
        return;
      }

      const fetchId = ++fetchIdRef.current;
      if (!background) setLoading(true);
      setError(null);

      try {
        const fyObj = (financialYear || []).find(
          (f) => Number(f.id) === Number(financialYearId)
        );
        const prevFyId = previousFinancialYearId(financialYear, fyObj);
        const { fromDate, toDate } = monthToDateRange(selectedMonth);
        const daysInMonth = getDaysInMonth(selectedMonth);

        const requestQuestionIds = extraAnalyticsQids.length
          ? Array.from(new Set([...questionIds, ...extraAnalyticsQids]))
          : questionIds;

        const [questions, triggers, analyticsRows] = await Promise.all([
          getReportingQuestions(financialYearId, ["quantitative"]),
          getTriggers(financialYearId),
          getOptimizedAnalyticsByLocation({
            financialYearId,
            previousFinancialYearId: prevFyId,
            locationIds: requestLocationIds,
            fromDate,
            toDate,
            questionIds: requestQuestionIds,
          }),
        ]);

        if (fetchId !== fetchIdRef.current) return;

        const cats = getCategories(questions, questionIds);

        const analyticsByScope = {};
        (analyticsRows || []).forEach((row) => {
          const key = scopeKey(row.locationId, row.subLocationId);
          if (!analyticsByScope[key]) analyticsByScope[key] = {};
          analyticsByScope[key][String(row.qid)] = row;
        });

        const nextByLocation = {};
        scopes.forEach((scope) => {
          const sid = scope.id;
          const subId = scope.subLocationId ?? null;
          const key = scopeKey(sid, subId);
          const analyticsMap = analyticsByScope[key] || {};
          const rows = cats.map((cat) => {
            const a = analyticsMap[String(cat.questionId)] || null;
            const actual = a ? a.selected_date_range_value : null;
            const { min, max } = triggerForMonth(
              triggers,
              cat.questionId,
              sid,
              fromDate,
              toDate,
              subId
            );
            return {
              questionId: cat.questionId,
              name: cat.name,
              min,
              max,
              actual,
              difference: computeDifference(actual, max),
              remark: computeRemark(actual, max),
              unit: (a && a.unit) || null,
            };
          });

          if (applyDerivedTriggers) {
            const triggerFor = (qid) =>
              triggerForMonth(triggers, qid, sid, fromDate, toDate, subId);
            applyDerivedTriggers(rows, analyticsMap, triggerFor, daysInMonth);
          }

          nextByLocation[key] = { rows };
        });

        setCategories(cats);
        setByLocation(nextByLocation);
      } catch (err) {
        if (fetchId !== fetchIdRef.current) return;
        console.error("Error loading analytics data:", err);
        setError("Failed to load data. Please try again.");
      } finally {
        if (fetchId === fetchIdRef.current && !background) setLoading(false);
      }
    },
    [
      financialYearId,
      selectedMonth,
      financialYear,
      questionIds,
      extraAnalyticsQids,
      applyDerivedTriggers,
    ]
  );

  useEffect(() => {
    runFetch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialYearId, selectedMonth, locationKey, runFetch]);

  const refresh = useCallback(() => runFetch(true), [runFetch]);

  return { categories, byLocation, loading, error, refresh };
};
